/*
 * Назначение: страница «Аналитика».
 * Функционал: показывает то, чего нет на дашборде — текстовую аналитику по
 * диалогам (раздел 17 ТЗ): топ вопросов клиентов, топ возражений и вопросы,
 * на которые ИИ не нашёл ответа в базе знаний (неотвеченные). Данные берутся
 * из /api/admin/dashboard и считаются по фактическим сообщениям за период.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, HelpCircle, Loader2, MessageCircleQuestion, ShieldQuestion } from "lucide-react";

import { dashboardApi } from "../../lib/admin-api";
import type { TopItem } from "../../lib/types";
import { useProject } from "../components/project/project-provider";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

function TopList({
  items,
  emptyText,
}: {
  items: TopItem[];
  emptyText: string;
}) {
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">{emptyText}</p>;
  }
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={idx} className="space-y-1">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm flex-1">{item.text}</span>
            <Badge variant="secondary" className="shrink-0">{item.count}</Badge>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary rounded-full"
              style={{ width: `${(item.count / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export function AnalyticsPage() {
  const { currentSlug } = useProject();
  const [period, setPeriod] = useState(30);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["analytics", currentSlug, period],
    queryFn: () => dashboardApi.overview(period),
    enabled: !!currentSlug,
  });

  return (
    <div className="p-3 sm:p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold">Аналитика</h2>
          <p className="text-sm text-muted-foreground">
            О чём спрашивают клиенты, какие возражения и где ИИ не хватает базы знаний.
          </p>
        </div>
        <Select value={String(period)} onValueChange={(v) => setPeriod(Number(v))}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">За 7 дней</SelectItem>
            <SelectItem value="30">За 30 дней</SelectItem>
            <SelectItem value="90">За 90 дней</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Загружаем…
        </div>
      ) : isError ? (
        <div className="flex items-center text-destructive">
          <AlertCircle className="h-5 w-5 mr-2" />
          Не удалось загрузить аналитику
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageCircleQuestion className="h-5 w-5" />
                Частые вопросы
              </CardTitle>
              <CardDescription>О чём чаще всего спрашивают клиенты</CardDescription>
            </CardHeader>
            <CardContent>
              <TopList
                items={data?.top_questions ?? []}
                emptyText="Пока недостаточно данных за период."
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldQuestion className="h-5 w-5" />
                Возражения
              </CardTitle>
              <CardDescription>Типовые сомнения клиентов</CardDescription>
            </CardHeader>
            <CardContent>
              <TopList
                items={data?.top_objections ?? []}
                emptyText="Возражений за период не зафиксировано."
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <HelpCircle className="h-5 w-5" />
                Не хватило базы знаний
              </CardTitle>
              <CardDescription>
                Вопросы, на которые ИИ не нашёл ответ и предложил менеджера. Стоит
                дополнить базу знаний.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TopList
                items={data?.unanswered ?? []}
                emptyText="Все вопросы пока находят ответ в базе знаний."
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
